import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { useSiteContent } from '@/context/SiteContentContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';
import StingrayAnimation from './components/StingrayAnimation';

const stats = [
  { value: '500+', label: 'Authorized Dealers' },
  { value: '50K+', label: 'Batteries in Service' },
  { value: '50+', label: 'Countries' },
  { value: '5yr', label: 'Lithium Warranty' },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Stingray',
  description: 'Stingray — global marine battery brand headquartered in Sheridan, Wyoming. Premium lithium, AGM and gel batteries distributed in 50+ countries.',
  url: 'https://stingray-marine.com/about',
  mainEntity: {
    '@type': 'Organization',
    name: 'Stingray',
    legalName: 'Cool Tech Marine Air Conditioning LLC',
    foundingLocation: 'Sheridan, Wyoming, USA',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: '10-50' },
    areaServed: 'Worldwide',
  },
};

export default function AboutPage() {
  const { theme } = useTheme();
  const { content } = useSiteContent();
  const { settings } = useBadgeVisibility();
  const anim = content.animation;
  const [customMediaFailed, setCustomMediaFailed] = useState(false);
  const showAnimationCanvas = anim.animationEnabled !== false;
  const visibleAnimationCards =
    anim.cardsEnabled !== false
      ? anim.cards.filter((item) => item.enabled !== false)
      : [];
  const showAnimationSection = anim.enabled && (showAnimationCanvas || visibleAnimationCards.length > 0);
  const customMediaUrl = anim.mediaUrl.trim();
  const useCustomAnimationMedia =
    showAnimationCanvas &&
    anim.mediaMode !== 'animation' &&
    customMediaUrl !== '' &&
    !customMediaFailed;
  const light = theme === 'light';

  useEffect(() => {
    setCustomMediaFailed(false);
  }, [anim.mediaMode, customMediaUrl]);

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const sectionBg = light ? 'bg-gray-100' : 'bg-[#111D2B]';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const bodyColor = light ? 'text-gray-600' : 'text-white/60';
  const cardBg = light ? 'bg-white border border-gray-200' : 'bg-[#1C2B3A]';
  const cardTextMuted = light ? 'text-gray-500' : 'text-white/50';
  const breadcrumbColor = light ? 'text-gray-400' : 'text-white/30';
  const breadcrumbActive = light ? 'text-gray-600' : 'text-white/60';
  const outlineBtn = light ? 'border border-gray-300 text-gray-700 hover:bg-gray-100' : 'border border-white/30 text-white hover:bg-white/10';

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title="About Stingray | Global Marine Power Brand"
        description="Stingray — premium marine battery brand headquartered in Sheridan, Wyoming. Lithium LiFePO4, AGM and gel batteries distributed in 50+ countries worldwide."
        keywords="about Stingray marine batteries, Cool Tech Marine, marine battery brand, global marine battery manufacturer"
        canonical="https://stingray-marine.com/about"
        schema={aboutSchema}
      />
      <Navbar />

      {/* Hero */}
      <header className="pt-28 pb-12 w-full px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          <div className="flex-1">
            <nav aria-label="Breadcrumb" className={`flex items-center gap-2 text-xs ${breadcrumbColor} mb-4`}>
              <a href="/" className="hover:text-[var(--accent)] transition-colors">Home</a>
              <i className="ri-arrow-right-s-line"></i>
              <span className={breadcrumbActive}>About</span>
            </nav>
            {settings.aboutBadge && (
              <span className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4" style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}>
                About Stingray
              </span>
            )}
            <h1 className={`text-4xl md:text-5xl font-black ${headingColor} mb-4 leading-tight`}>
              Powering the World&apos;s<br />
              <span style={{ color: 'var(--accent)' }}>Marine Community</span>
            </h1>
            <p className={`${bodyColor} text-base leading-relaxed mb-4`}>
              <strong className={light ? 'text-gray-900' : 'text-white'}>Stingray</strong> is headquartered in Sheridan, Wyoming, with a global distribution network spanning over 50 countries.
            </p>
            <p className={`${bodyColor} text-base leading-relaxed mb-8`}>
              From weekend anglers to commercial charter captains, Stingray batteries power vessels across every ocean — from the Caribbean to the Mediterranean, the Pacific to the North Sea.
            </p>
            <div className="flex flex-wrap gap-6 mb-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="text-2xl font-black" style={{ color: 'var(--accent)' }}>{s.value}</p>
                  <p className={`${cardTextMuted} text-xs`}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/products" className="text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide" style={{ background: 'var(--accent)' }}>
                View Products
              </Link>
              <Link to="/contact" className={`${outlineBtn} font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide`}>
                Contact Us
              </Link>
            </div>
          </div>
          <div className="flex-1">
            <div className="w-full h-[360px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=wyoming%20sheridan%20american%20business%20office%20building%20exterior%20modern%20clean%20professional%20corporate%20headquarters%20sunny%20day%20blue%20sky%20american%20flag%20commercial%20real%20estate%20photography&width=700&height=500&seq=about-hq&orientation=landscape"
                alt="Stingray headquarters in Sheridan, Wyoming"
                title="Stingray — Cool Tech Marine HQ, Sheridan WY"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </header>

      {/* ── Stingray Animation Section ── */}
      {showAnimationSection && (
        <section className="w-full px-6 md:px-10 py-16">
          <div className="text-center mb-8">
            {settings.animationBadge && (
              <span
                className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4"
                style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
              >
                {anim.badge}
              </span>
            )}
            <h2 className={`text-3xl md:text-4xl font-black ${headingColor} mb-3`}>
              {anim.headline1}<br />
              <span style={{ color: 'var(--accent)' }}>{anim.headline2}</span>
            </h2>
            <p className={`${bodyColor} text-base max-w-xl mx-auto`}>
              {anim.subtitle}
            </p>
          </div>
          {showAnimationCanvas && (
            useCustomAnimationMedia ? (
              <div className={`relative w-full rounded-2xl overflow-hidden ${light ? 'bg-white border border-gray-200' : 'bg-[#071525] border border-white/10'}`} style={{ height: 420 }}>
                {anim.mediaMode === 'image' ? (
                  <img
                    src={customMediaUrl}
                    alt="Stingray animation replacement"
                    className="w-full h-full object-cover object-center"
                    onError={() => setCustomMediaFailed(true)}
                  />
                ) : (
                  <video
                    src={customMediaUrl}
                    className="w-full h-full object-cover object-center"
                    autoPlay
                    loop
                    muted
                    playsInline
                    onError={() => setCustomMediaFailed(true)}
                  />
                )}
              </div>
            ) : (
              <StingrayAnimation />
            )
          )}
          {visibleAnimationCards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              {visibleAnimationCards.map((item, i) => (
                <div key={i} className={`${cardBg} rounded-lg p-6 flex gap-4 items-start`}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg shrink-0" style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                    <i className={`${item.icon} text-xl`} style={{ color: 'var(--accent)' }}></i>
                  </div>
                  <div>
                    <h3 className={`${headingColor} font-bold text-sm mb-1`}>{item.title}</h3>
                    <p className={`${cardTextMuted} text-xs leading-relaxed`}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Values */}
      <section className={`${sectionBg} w-full py-12 transition-colors duration-300`}>
        <div className="w-full px-6 md:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: 'ri-shield-check-line', title: 'Quality First', desc: 'Engineered to exceed industry standards.' },
              { icon: 'ri-anchor-line', title: 'Marine Expertise', desc: 'Designed for saltwater & freshwater demands.' },
              { icon: 'ri-leaf-line', title: 'Sustainability', desc: 'Non-toxic LiFePO4, 10x longer life.' },
              { icon: 'ri-global-line', title: 'Global Network', desc: '500+ authorized dealers in 50+ countries.' },
            ].map((v, i) => (
              <div key={i} className={`${cardBg} rounded-lg p-5 flex gap-4 items-start`}>
                <div className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0" style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                  <i className={`${v.icon} text-lg`} style={{ color: 'var(--accent)' }}></i>
                </div>
                <div>
                  <h3 className={`${headingColor} font-bold text-sm mb-1`}>{v.title}</h3>
                  <p className={`${cardTextMuted} text-xs leading-relaxed`}>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
